import { Loader2 } from "lucide-react";
import { FC } from "react";
import { FormattedMessage } from "react-intl";
import type {
  PreviewPlanChange200,
  PreviewPlanChangeBodyOrganizationPlan,
} from "~/api/model";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";

type PlanChangeModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preview: PreviewPlanChange200 | undefined;
  isLoadingPreview: boolean;
  isChangingPlan: boolean;
  currentPlan: string | null;
  targetPlan: PreviewPlanChangeBodyOrganizationPlan | null;
  onConfirm: () => void;
};

const formatCurrency = (amountInCents: number, currency: string) => {
  const amount = Math.abs(amountInCents) / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount);
};

const formatDate = (timestamp: number) => {
  return new Date(timestamp * 1000).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const PLAN_RANK: Record<string, number> = {
  FLEX: 0,
  PRO: 1,
  PREMIUM: 2,
};

export const PlanChangeModal: FC<PlanChangeModalProps> = ({
  open,
  onOpenChange,
  preview,
  isLoadingPreview,
  isChangingPlan,
  currentPlan,
  targetPlan,
  onConfirm,
}) => {
  const isUpgrade =
    targetPlan !== null &&
    (PLAN_RANK[targetPlan] ?? -1) > (PLAN_RANK[currentPlan ?? ""] ?? -1);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage
              id={
                isUpgrade
                  ? "billing.modal.upgradeTitle"
                  : "billing.modal.downgradeTitle"
              }
              defaultMessage={isUpgrade ? "Upgrade Plan" : "Downgrade Plan"}
            />
          </DialogTitle>
          <DialogDescription>
            <FormattedMessage
              id="billing.modal.description"
              defaultMessage="Review the pricing changes before confirming."
            />
          </DialogDescription>
        </DialogHeader>

        {isLoadingPreview ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : preview ? (
          <div className="space-y-3">
            <div className="rounded-lg border bg-muted/50 p-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  <FormattedMessage
                    id="billing.modal.planChange"
                    defaultMessage="Plan"
                  />
                </span>
                <span className="font-medium">
                  {preview.currentPlan} → {preview.newPlan}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  <FormattedMessage
                    id="billing.modal.billingPeriodEnd"
                    defaultMessage="Current period ends"
                  />
                </span>
                <span className="font-medium">
                  {formatDate(preview.periodEnd)}
                </span>
              </div>
            </div>

            <div className="rounded-lg border bg-muted/50 p-3">
              {preview.immediateCredit < 0 && (
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-muted-foreground">
                    <FormattedMessage
                      id="billing.modal.credit"
                      defaultMessage="Credit from current plan"
                    />
                  </span>
                  <span className="text-green-600 dark:text-green-400">
                    -{formatCurrency(preview.immediateCredit, preview.currency)}
                  </span>
                </div>
              )}
              {preview.immediateCharge > 0 && (
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-muted-foreground">
                    <FormattedMessage
                      id="billing.modal.charge"
                      defaultMessage="Charge for new plan"
                    />
                  </span>
                  <span>
                    {formatCurrency(preview.immediateCharge, preview.currency)}
                  </span>
                </div>
              )}
              <div className="flex justify-between font-medium border-t pt-1.5">
                <span>
                  {preview.netAmount < 0 ? (
                    <FormattedMessage
                      id="billing.modal.totalCredit"
                      defaultMessage="Credit to your account"
                    />
                  ) : (
                    <FormattedMessage
                      id="billing.modal.total"
                      defaultMessage="Amount due today"
                    />
                  )}
                </span>
                <span
                  className={
                    preview.netAmount < 0
                      ? "text-green-600 dark:text-green-400"
                      : ""
                  }
                >
                  {formatCurrency(preview.netAmount, preview.currency)}
                </span>
              </div>
            </div>

            {preview.lineItems.length > 0 && (
              <Accordion type="single" collapsible>
                <AccordionItem value="proration" className="border-none">
                  <AccordionTrigger className="py-1 text-xs text-muted-foreground hover:no-underline">
                    <FormattedMessage
                      id="billing.modal.prorationDetails"
                      defaultMessage="Proration details"
                    />
                  </AccordionTrigger>
                  <AccordionContent className="pb-0 pt-0">
                    <div className="overflow-hidden rounded-lg border p-2.5 space-y-1.5">
                      {preview.lineItems.map((item, index) => (
                        <div
                          key={index}
                          className="flex justify-between text-xs gap-3"
                        >
                          <span className="min-w-0 text-muted-foreground">
                            {item.description}
                          </span>
                          <span
                            className={
                              (item.amount ?? 0) < 0
                                ? "flex-shrink-0 text-green-600 dark:text-green-400 whitespace-nowrap"
                                : "flex-shrink-0 whitespace-nowrap"
                            }
                          >
                            {(item.amount ?? 0) < 0 ? "-" : ""}
                            {formatCurrency(
                              item.amount ?? 0,
                              item.currency ?? preview.currency
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}

            {preview.netAmount < 0 && (
              <p className="text-xs text-muted-foreground">
                <FormattedMessage
                  id="billing.modal.creditNote"
                  defaultMessage="The credit will be applied to your next invoice."
                />
              </p>
            )}
          </div>
        ) : (
          <div className="py-4 text-center text-sm text-muted-foreground">
            <FormattedMessage
              id="billing.modal.previewError"
              defaultMessage="Unable to load pricing preview. Please try again."
            />
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isChangingPlan}
          >
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isLoadingPreview || !preview || isChangingPlan}
          >
            {isChangingPlan ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            <FormattedMessage
              id={
                isUpgrade
                  ? "billing.modal.confirmUpgrade"
                  : "billing.modal.confirmDowngrade"
              }
              defaultMessage={
                isUpgrade ? "Confirm Upgrade" : "Confirm Downgrade"
              }
            />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
